/**
 * Where browser analytics goes right now.
 *
 * - `live`: production. GTM (head snippet) and Mixpanel (npm lib) are real.
 * - `capture`: a STAGING tab opted in with `?nt_analytics_capture=1`. Pushes
 *   land in `window.dataLayer` (no container reads it) and Mixpanel calls are
 *   recorded in `window.__ntMixpanelCapture`. Nothing leaves the browser. Used
 *   to prove each event fires exactly once (plan ob-07, Task 14).
 * - `off`: everything else.
 */
import { isAnalyticsEnabled } from '@/config'

export type AnalyticsMode = 'live' | 'capture' | 'off'

export const ANALYTICS_CAPTURE_PARAM = 'nt_analytics_capture'
export const MIXPANEL_CAPTURE_GLOBAL = '__ntMixpanelCapture'
const CAPTURE_STORAGE_KEY = 'nt_analytics_capture'

function readCaptureFlag(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const value = new URLSearchParams(window.location?.search ?? '').get(ANALYTICS_CAPTURE_PARAM)
    if (value === '1') window.sessionStorage.setItem(CAPTURE_STORAGE_KEY, '1')
    if (value === '0') window.sessionStorage.removeItem(CAPTURE_STORAGE_KEY)
    return window.sessionStorage.getItem(CAPTURE_STORAGE_KEY) === '1'
  } catch {
    // Storage blocked: capture is a debugging aid, never worth an error.
    return false
  }
}

export function analyticsMode(): AnalyticsMode {
  if (isAnalyticsEnabled()) return 'live'
  if (process.env.VITE_APP_ENVIRONMENT === 'staging' && readCaptureFlag()) return 'capture'
  return 'off'
}

export function isAnalyticsActive(): boolean {
  return analyticsMode() !== 'off'
}

export function recordMixpanelCapture(event: string, properties: unknown): void {
  if (typeof window === 'undefined') return
  const target = window as unknown as Record<string, unknown>
  const list = Array.isArray(target[MIXPANEL_CAPTURE_GLOBAL])
    ? (target[MIXPANEL_CAPTURE_GLOBAL] as Array<{ event: string; properties: unknown }>)
    : []
  list.push({ event, properties })
  target[MIXPANEL_CAPTURE_GLOBAL] = list
}
